/**
 * Options for the "Reason" dropdown on PIA and Plan F approvals when the approved
 * terms differ from the applied-for terms. The list was supplied with ticket
 * RP-8750.
 *
 * The chosen labels are saved as text inside `otafDescription`, joined with ", ",
 * and the free text typed under "Other" is saved last, wrapped in double quotes.
 * So a label must never contain a double quote or a comma.
 */
export const PIA_PLAN_F_APPROVAL_REASONS: readonly string[] = [
  'Abnormal Blood Pressure',
  'Alcohol use',
  'Anemia/Blood disorders',
  'Anxiety Disorders',
  'Asthma',
  'Autism',
  'Autoimmune disorders',
  'Aviation',
  'Avocations / Hazardous Hobbies',
  'Benign Tumor/Mass',
  'Bronchitis',
  'Build/Weight change',
  'Cancer',
  'Circulatory (Blood Vessels) Disorders',
  'Citizenship status / Visa/EAD',
  'Connective tissue disorders',
  'Criminal history',
  'Diabetes',
  'Digestive disorders',
  'Disability',
  'Driving history',
  'Ear Disorder',
  'Endocrine/Hormonal disorders',
  'Epilepsy',
  'Existing Insurance Coverage',
  'Eye Disorder',
  'Family History',
  'Fatty Liver - Alcoholic / Non-Alcoholic',
  'Financial Eligibility not met',
  'Findings in Blood test',
  'Findings in Diagnostic Testing',
  'Foreign Residency',
  'Foreign travel',
  'Gastrointestinal disorders',
  'Genetic Disorder',
  'Genital disorder',
  'Heart or Circulatory disorders',
  'High Blood pressure',
  'High Cholesterol / Abnormal Lipids',
  'Immune disorders',
  'Insufficient Family Insurance Coverage',
  'Kidney disorder',
  'Liver Disease or Disorder',
  'Marijuana use',
  'Mental or psychiatric disorders',
  'Muscle/Bone/Joint Disorders',
  'Neurological / Nervous System Disorders',
  'Nicotine use',
  'Occupation / Occupation - Military',
  'Pending Diagnostic/Lab Testing',
  'Recent Hospitalization / Hospitalization History / Surgery',
  'Reproductive / Gynaecological Disorder',
  'Respiratory disorders',
  'Skin Disorder',
  'Sleep Apnea / Obstructive Sleep Apnea',
  'Stroke',
  'Substance use',
  'Thyroid Disorder',
  'Urinary Bladder disorder',
  'Urinary Disorder',
];

/** Last option. Selecting it reveals a free-text box. */
export const OTHER_APPROVAL_REASON = 'Other';

export const piaPlanFApprovalReasonOptions = [
  ...PIA_PLAN_F_APPROVAL_REASONS,
  OTHER_APPROVAL_REASON,
].map(reason => ({ value: reason, label: reason }));

const SEPARATOR = ', ';
const QUOTE = '"';
const SINGLE_QUOTE = `'`;

export interface ApprovalReasonSelection {
  /** Selected options. Includes OTHER_APPROVAL_REASON when there is other text. */
  selected: string[];
  otherText: string;
}

export const hasQuote = (text: string): boolean => text.includes(QUOTE);

/**
 * The Reason is mandatory: at least one option, and when "Other" is selected its
 * text must be filled in and must not contain a double quote.
 */
export const isApprovalReasonValid = (
  selected: readonly string[],
  otherText: string,
): boolean => {
  if (selected.length === 0) {
    return false;
  }
  if (!selected.includes(OTHER_APPROVAL_REASON)) {
    return true;
  }
  return otherText.trim() !== '' && !hasQuote(otherText);
};

/**
 * Builds the text saved in `otafDescription`: the chosen labels in dropdown-list
 * order, then the trimmed "Other" text in double quotes. Unknown selections are
 * ignored. The other text is left out when "Other" is not selected.
 */
export const buildApprovalReasonText = (
  selected: readonly string[],
  otherText: string,
): string => {
  const labels = PIA_PLAN_F_APPROVAL_REASONS.filter(reason =>
    selected.includes(reason),
  );
  const other = selected.includes(OTHER_APPROVAL_REASON)
    ? otherText.trim()
    : '';
  if (hasQuote(other)) {
    throw new Error('The other reason cannot contain a double quote');
  }
  return [...labels, ...(other ? [`${QUOTE}${other}${QUOTE}`] : [])].join(
    SEPARATOR,
  );
};

const parseLabels = (text: string): string[] | null => {
  const labels = text.split(SEPARATOR);
  return labels.every(label => PIA_PLAN_F_APPROVAL_REASONS.includes(label))
    ? labels
    : null;
};

const parseBuiltText = (text: string): ApprovalReasonSelection | null => {
  const firstQuote = text.indexOf(QUOTE);
  if (firstQuote === -1) {
    const onlyLabels = parseLabels(text);
    return onlyLabels && { selected: onlyLabels, otherText: '' };
  }

  if (!text.endsWith(QUOTE) || firstQuote === text.length - 1) {
    return null;
  }
  const otherText = text.slice(firstQuote + 1, -1);
  if (!otherText || otherText !== otherText.trim() || hasQuote(otherText)) {
    return null;
  }

  const before = text.slice(0, firstQuote);
  if (before === '') {
    return { selected: [OTHER_APPROVAL_REASON], otherText };
  }
  if (!before.endsWith(SEPARATOR)) {
    return null;
  }
  const labels = parseLabels(before.slice(0, -SEPARATOR.length));
  return labels && { selected: [...labels, OTHER_APPROVAL_REASON], otherText };
};

/**
 * Reads the text saved in `otafDescription` back into the dropdown selections.
 * Text that was not built by `buildApprovalReasonText` (free text from before the
 * dropdown existed) is returned whole as the "Other" text, with any double quote
 * replaced by a single quote so it can be submitted again unchanged.
 */
export const parseApprovalReasonText = (
  text: string | null | undefined,
): ApprovalReasonSelection => {
  if (!text) {
    return { selected: [], otherText: '' };
  }
  return (
    parseBuiltText(text) ?? {
      selected: [OTHER_APPROVAL_REASON],
      otherText: text.split(QUOTE).join(SINGLE_QUOTE),
    }
  );
};
